import React from 'react';
import {
  MoreActions,
  MoreActionsItem,
  OpenSidebarButton,
  OpenSidebarPayload,
  isAccess,
  isResourceAccess,
  useSidebar,
} from '@css-xp/base';
import { Permissions, Resources } from '@atlassian/cssxp-typescommon';
import { EditBillingDetails } from './sidebar-panels/edit-billing-details-sidebar';
import { SIDEBAR } from './transaction-account-page';

interface Props {
  editBillingDetailsFormProps: EditBillingDetails;
  isPartner: boolean;
  hasPaymentOnTerms: boolean;
  isActive: boolean;
  enableTxaRevampFlag?: boolean;
}

const TransactionAccountActions = ({
  editBillingDetailsFormProps,
  isPartner,
  isActive,
  enableTxaRevampFlag = false,
}: Props) => {
  const { openSidebar } = useSidebar();
  const handleSidebarAction = (openSidebarPayload: OpenSidebarPayload) => {
    openSidebar(openSidebarPayload);
  };
  const transactionAccountActions: MoreActionsItem[] = [
    {
      id: 'create Invoice Group',
      content: 'Create Invoice Group',
      isDisabled: !isActive,
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.CREATE_INVOICE_GROUP,
          payload: {
            transactionAccountId: editBillingDetailsFormProps.transactionAccountId,
            billToAddress: editBillingDetailsFormProps.billToAddress,
            refetch: editBillingDetailsFormProps.onComplete,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '50%',
          },
        }),

      predicate:
        !isPartner && isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && isAccess(Permissions.INVOICE_GROUP_WRITE),
    },
    {
      id: 'set pricing eligibility',
      content: 'Set Pricing Eligibility',
      isDisabled: !isActive,
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.SET_PRICING_ELIGIBILITY,
          payload: {
            transactionAccountId: editBillingDetailsFormProps.transactionAccountId,
            pricingEligibility: editBillingDetailsFormProps.billToPartyPricingEligibilty,
            billToAddress: editBillingDetailsFormProps.billToAddress,
            refetch: editBillingDetailsFormProps.onComplete,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '50%',
          },
        }),
      predicate:
        true &&
        isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) &&
        isAccess(Permissions.BILL_TO_PRICING_ELIGIBILITY_WRITE),
    },
    {
      id: 'create ship to party',
      content: 'Create ship to party',
      isDisabled: !isActive,
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.EDIT_SHIP_TO_PARTY,
          payload: {
            transactionAccountId: editBillingDetailsFormProps.transactionAccountId,
            billToAddress: editBillingDetailsFormProps.billToAddress,
            refetch: editBillingDetailsFormProps.onComplete,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '50%',
          },
        }),

      predicate:
        true && isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && isAccess(Permissions.SHIP_TO_PARTY_WRITE),
    },
    {
      id: 'add payment method',
      content: 'Add payment on terms',
      isDisabled: !isActive,
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.ADD_PAYMENT_ON_TERMS_METHOD,
          payload: {
            transactionAccountId: editBillingDetailsFormProps.transactionAccountId,
            refetch: editBillingDetailsFormProps.onComplete,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '50%',
          },
        }),

      predicate:
        true &&
        isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) &&
        isAccess(Permissions.PAYMENT_METHOD_ON_TERM_WRITE),
    },
    {
      id: 'set payment method',
      content: 'Set default payment method',
      isDisabled: !isActive,
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.SET_CARD_PAYMENT_DEFAULT,
          payload: {
            transactionAccountId: editBillingDetailsFormProps.transactionAccountId,
            refetch: editBillingDetailsFormProps.onComplete,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '50%',
          },
        }),

      predicate:
        true &&
        isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) &&
        isAccess(Permissions.PAYMENT_METHOD_DEFAULT_WRITE),
    },
  ];

  return (
    <div className="button-toolbar" data-testid="header-actions">
      {isAccess(Permissions.TRANSACTION_ACCOUNT_WRITE) && isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && (
        <>
          <OpenSidebarButton
            sidebar={SIDEBAR.CREATE_TRANSACTION_ACCOUNT}
            payload={{
              ...editBillingDetailsFormProps,
            }}
            sidebarOptions={{
              sidebarWidth: '50%',
              isOverlay: true,
            }}
            testId="create-transaction-account-action"
            label={enableTxaRevampFlag ? 'Create new' : 'Create transaction account'}
          >
            {enableTxaRevampFlag ? 'Create new' : 'Create transaction account'}
          </OpenSidebarButton>
          <OpenSidebarButton
            sidebar={SIDEBAR.EDIT_TRANSACTION_ACCOUNT}
            payload={{
              ...editBillingDetailsFormProps,
            }}
            appearance={enableTxaRevampFlag ? 'primary' : 'default'}
            sidebarOptions={{
              sidebarWidth: '50%',
              isOverlay: true,
            }}
            testId="edit-transaction-account-action"
            label={enableTxaRevampFlag ? 'Edit details' : 'Edit transaction account'}
          >
            {enableTxaRevampFlag ? 'Edit details' : 'Edit transaction account'}
          </OpenSidebarButton>
        </>
      )}
      <MoreActions actions={transactionAccountActions} />
    </div>
  );
};

export { TransactionAccountActions };
